import { AppDispatch } from "@Reducers/index";
import axiosWrapper from "../services/axiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import { setUserPermissions, setPortalSettingInfo, setPortalSettingInfoRead } from "../reducers/userPermissions";
import type { GetUserPermissionsRequest } from "@ServerTypes/permissions/permissions.schema";
import type {
  GetNotificationPreferenceRequest,
  SaveNotificationPreferenceRequest,
  SaveUserSettingsRequest,
  GetUserSettingsRequest,
  DeleteUserSettingsRequest,
} from "@ServerTypes/userSettings/userSettings.schema";

export function getUserPermissions<T extends Exact<GetUserPermissionsRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: this thunk is declared to return void, so every `dispatch(getUserPermissions(…))`
    // call site gets void back and has nothing to await; the permissions arrive through the store.
    void axiosWrapper.api.post("/permissions/getUserPermissions", data).then((response) => {
      // `result` (keyed by requested resourceType, each `{ permissions }`) rides the success arm only;
      // on a failure the slice keeps its initial `{}`, which reads as "no permission granted".
      if (response.data.status) {
        dispatch(setUserPermissions(response.data.result));
      }
    });
  };
}
export const getNotificationPreference = async <T extends Exact<GetNotificationPreferenceRequest, T>>(data: T) => {
  const response = await axiosWrapper.api.post("/userSettings/getNotificationPreference", data);
  return response;
};

export const saveNotificationPreference = async <T extends Exact<SaveNotificationPreferenceRequest, T>>(data: T) => {
  const response = await axiosWrapper.api.post("/userSettings/saveNotificationPreference", data);
  return response;
};

export function getPortalSetting() {
  return async (dispatch: AppDispatch) => {
    try {
      const responseData = await axiosWrapper.api.get("/portal/getPortalSettingsObject");
      // Only dispatch when the body actually carried the settings object: `data` is `.optional()`
      // even on the success arm, while the slice's declared state is non-nullable
      // `PortalSettingInfoData`, which is what makes every consumer's `portalSettingInfo?.` guard
      // load-bearing at runtime. Keeping the initial `{}` is the correct fallback: every permission
      // flag then reads as undefined, i.e. "not granted".
      if (responseData.data.status && responseData.data.result) {
        dispatch(setPortalSettingInfo(responseData.data.result));
        dispatch(setPortalSettingInfoRead("done"));
        return;
      }
      dispatch(setPortalSettingInfoRead("failed"));
    } catch (error) {
      // The read has ended either way (screens waiting on it settle, every gate closed); the rethrow keeps today's
      // unhandled-rejection report of the failed request.
      dispatch(setPortalSettingInfoRead("failed"));
      throw error;
    }
  };
}
export const saveUserSettings = async <T extends Exact<SaveUserSettingsRequest, T>>(data: T) => {
  const response = axiosWrapper.api.post("/userSettings/saveUserSettings", data);
  return response;
};
export const getUserSettings = async <T extends Exact<GetUserSettingsRequest, T>>(data: T) => {
  const response = axiosWrapper.api.post("/userSettings/getUserSettings", data);
  return response;
};
export const deleteUserSettings = async <T extends Exact<DeleteUserSettingsRequest, T>>(data: T) => {
  const response = axiosWrapper.api.post("/userSettings/deleteUserSettings", data);
  return response;
};
